import { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Navbar from "@/components/Navbar";
import SEO from "@/components/SEO";
import Footer from "@/components/Footer";
import axios from "axios";
import { ArrowRight, Calendar, Eye } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Structured category list — subcategories appear visually nested under their parent
const CATEGORY_TREE = [
  { label: "All" },
  { label: "Naval History" },
  { label: "Political Science", subs: ["Voting"] },
  { label: "AD0K Radio" },
  { label: "Music" },
  { label: "Writing" },
  { label: "Misc" },
];
// Flat list for any logic that needs it
const ALL_CATS = CATEGORY_TREE.flatMap(c => [c.label, ...(c.subs || [])]);

function formatDate(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
}

export default function Blog() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();
  const activeCategory = searchParams.get("category") || "All";

  useEffect(() => {
    document.title = "The Captain's Log — Donald Stephen Inbody on Naval History, Political Science & More";
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = activeCategory !== "All" ? `?category=${encodeURIComponent(activeCategory)}` : "";
    axios.get(`${API}/blog/posts${params}`)
      .then(r => setPosts(r.data))
      .catch(() => setPosts([]))
      .finally(() => setLoading(false));
  }, [activeCategory]);

  return (
    <div className="bg-[#F5F0E8]">
      <SEO
        title="The Captain's Log"
        description="The Captain's Log — Donald Stephen Inbody's blog covering Naval History, WWI Naval History, Political Science, military voting rights, amateur radio (ADØK), and Americana music with BuffaloGrass."
        canonical="https://inbody.net/blog"
        keywords="The Captain's Log, naval history blog, WWI Naval History, Historical Accuracy, Donald Stephen Inbody blog, Voting, Political Science blog, military voting blog, AD0K radio blog, BuffaloGrass music"
      />
      <Navbar />

      {/* Header */}
      <section className="pt-32 pb-16 border-b border-[#0A1628]/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <p className="overline mb-3">Writing &amp; Thoughts</p>
          <h1 data-testid="blog-page-title" className="font-serif text-5xl font-black text-[#0A1628] mb-4">
            The Captain's Log
          </h1>
          <p className="text-[#2D3142] max-w-xl leading-relaxed">
            Reflections on naval history, political science, amateur radio adventures, and music.
          </p>
        </div>
      </section>

      {/* Category Filter */}
      <div className="bg-white border-b border-[#0A1628]/10 sticky top-16 z-40">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div
            data-testid="blog-category-filter"
            className="flex items-center gap-1 overflow-x-auto py-4"
          >
            {CATEGORY_TREE.map(cat => (
              <div key={cat.label} className="flex items-center gap-0.5">
                <button
                  data-testid={`category-btn-${cat.label.toLowerCase().replace(/ /g, '-')}`}
                  onClick={() => setSearchParams(cat.label === "All" ? {} : { category: cat.label })}
                  className={`whitespace-nowrap px-4 py-2 text-sm font-medium transition-colors duration-200 ${
                    activeCategory === cat.label
                      ? "bg-[#0A1628] text-[#F5F0E8]"
                      : "text-[#2D3142] hover:bg-[#F5F0E8]"
                  }`}
                >
                  {cat.label}
                </button>
                {cat.subs?.map(sub => (
                  <button
                    key={sub}
                    data-testid={`category-btn-${sub.toLowerCase().replace(/ /g, '-')}`}
                    onClick={() => setSearchParams({ category: sub })}
                    className={`whitespace-nowrap pl-3 pr-4 py-2 text-xs font-medium transition-colors duration-200 border-l-2 border-[#C5A059]/40 ${
                      activeCategory === sub
                        ? "bg-[#C5A059]/20 text-[#0A1628] border-[#C5A059]"
                        : "text-[#2D3142]/60 hover:bg-[#F5F0E8] hover:text-[#2D3142]"
                    }`}
                  >
                    {sub}
                  </button>
                ))}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Posts Grid */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          {loading ? (
            <div className="flex justify-center py-16">
              <div className="w-8 h-8 border-2 border-[#0A1628] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : posts.length === 0 ? (
            <div data-testid="blog-empty" className="text-center py-24">
              <p className="font-serif text-2xl text-[#0A1628] mb-3">No posts yet in this category</p>
              <p className="text-[#2D3142]">Check back soon — more is coming.</p>
            </div>
          ) : (
            <div data-testid="blog-posts-grid" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {posts.map((post, i) => (
                <Link
                  key={post.id}
                  to={`/blog/${post.slug}`}
                  data-testid={`blog-post-card-${i}`}
                  className="bento-card bg-white p-6 flex flex-col group"
                >
                  <div className="mb-4">
                    <span className="inline-block bg-[#0A1628] text-[#C5A059] text-xs font-semibold px-3 py-1 uppercase tracking-wider">
                      {(post.categories || [post.category]).map(cat => (
                        <span key={cat} className="text-[10px] font-semibold tracking-widest uppercase text-[#C5A059]">{cat}</span>
                      ))}
                    </span>
                  </div>
                  <h2 className="font-serif text-xl font-bold text-[#0A1628] mb-3 leading-snug group-hover:text-[#2D3142] transition-colors">
                    {post.title}
                  </h2>
                  <p className="text-[#2D3142] text-sm leading-relaxed flex-1 mb-4 line-clamp-3">
                    {post.excerpt}
                  </p>
                  <div className="flex items-center justify-between mt-auto">
                    <div className="flex items-center gap-3 text-[#2D3142]/50 text-xs">
                      <span className="flex items-center gap-1.5">
                        <Calendar size={12} />
                        {formatDate(post.created_at)}
                      </span>
                      {post.views !== undefined && (
                        <span className="flex items-center gap-1.5">
                          <Eye size={12} />
                          {post.views} reads
                        </span>
                      )}
                    </div>
                    <span className="inline-flex items-center gap-1 text-[#0A1628] text-xs font-semibold group-hover:gap-2 transition-all">
                      Read <ArrowRight size={12} />
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}
